import React, { useEffect, useState } from "react"

import { GET, httpOk } from "helpers/api"
import { formatDate } from "helpers/helpers"

import { Card, EmptyNote, Skeleton, TabError, cardStyle, formatNumber, mutedStyle } from "../parts/common"

const MONTHS_BACK = 6
const MONTH_NAMES = ["январь", "февраль", "март", "апрель", "май", "июнь", "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"]

const STATUS_LABELS = {
	1: { text: "Действует", color: "#1f9d57" },
	"-1": { text: "Блок за неоплату", color: "#d23434" },
	"-3": { text: "Удалена", color: "#8791a1" },
}

const GROUPS = [
	{ id: "unpaid", title: "Не платили", hint: "в этом месяце не было ни одного пополнения", accent: "#d23434" },
	{ id: "paid", title: "Платили, но чеков нет", hint: "деньги внесли, а чеки не пробивают", accent: "#f0a04c" },
]

/** Список месяцев для выбора: текущий и MONTHS_BACK предыдущих, от свежего к старому. */
function monthOptions() {
	const now = new Date()
	const result = []
	for (let index = 0; index <= MONTHS_BACK; index++) {
		const date = new Date(now.getFullYear(), now.getMonth() - index, 1)
		const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
		result.push({ value, label: `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}` })
	}
	return result
}

/**
 * Потери месяца — кто ушёл (последний чек был 14+ дней назад) и почему: не платит или
 * платит, но не пробивает чеки. Расчёт на бэке — LostPointsService, данные из свёртки.
 */
function Lost() {
	const months = monthOptions()

	const [month, setMonth] = useState(months[0].value)
	const [group, setGroup] = useState(null)
	const [data, setData] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	async function load(value) {
		setLoading(true)
		setError(null)
		const response = await GET("/admin/analytics/lost", { month: value }, { notification: false })
		if (httpOk(response)) {
			setData(response.data)
		} else {
			setError(response?.data?.message || "Не удалось загрузить данные")
		}
		setLoading(false)
	}

	useEffect(() => {
		load(month)
	}, [month]) // eslint-disable-line react-hooks/exhaustive-deps

	const summary = data?.summary || {}
	const points = (data?.points || []).filter((point) => !group || point.group === group)

	return (
		<>
			<div className="d-flex align-items-center flex-wrap gap-2 mb-3">
				<select className="form-select form-select-sm" style={{ width: "180px" }}
					value={month} onChange={(event) => setMonth(event.target.value)}>
					{months.map((option) => (
						<option key={option.value} value={option.value}>{option.label}</option>
					))}
				</select>
				<span style={mutedStyle}>
					Ушла = последний чек был {data?.silence_days || 14}+ дней назад и после него чеков нет.
					{data?.is_current && " Месяц ещё идёт — к его концу цифры вырастут."}
				</span>
			</div>

			{error && <TabError message={error} onRetry={() => load(month)} />}
			{!error && (loading || !data) && <Skeleton rows={3} />}

			{!error && !loading && data && (
				<>
					<div className="row g-2 mb-3">
						<div className="col-md-4">
							<div className="card h-100 mb-0" style={cardStyle}>
								<div className="card-body py-3 px-3">
									<div style={mutedStyle}>Ушло за месяц</div>
									<h3 className="mb-0 mt-2" style={{ fontSize: "22px", fontWeight: 800 }}>{formatNumber(summary.total)}</h3>
									<div style={mutedStyle}>тариф к списанию: {formatNumber(summary.tariff_at_risk)} сум/мес</div>
								</div>
							</div>
						</div>
						{GROUPS.map((item) => (
							<div className="col-md-4" key={item.id}>
								<div className="card h-100 mb-0" role="button"
									style={{ ...cardStyle, outline: group === item.id ? `2px solid ${item.accent}` : "none" }}
									onClick={() => setGroup(group === item.id ? null : item.id)}>
									<div className="card-body py-3 px-3">
										<div style={mutedStyle}>{item.title}</div>
										<h3 className="mb-0 mt-2" style={{ fontSize: "22px", fontWeight: 800, color: item.accent }}>
											{formatNumber(summary[item.id])}
										</h3>
										<div style={mutedStyle}>{item.hint}</div>
									</div>
								</div>
							</div>
						))}
					</div>

					<Card title="Ушедшие точки" right={<span style={mutedStyle}>нажмите на карточку, чтобы отфильтровать</span>}>
						{points.length === 0 && <EmptyNote>В этом месяце потерь нет</EmptyNote>}
						{points.length > 0 && (
							<div className="table-responsive">
								<table className="table table-sm table-hover table-bordered mb-0" style={{ fontSize: "12px" }}>
									<thead>
										<tr>
											<th>Точка</th>
											<th>Агент</th>
											<th>Статус</th>
											<th>Группа</th>
											<th className="text-center">Последний чек</th>
											<th className="text-end">Дней молчит</th>
											<th className="text-center">Последняя оплата</th>
											<th className="text-end">Оплачено в месяце</th>
											<th className="text-end">Баланс</th>
											<th className="text-end">Тариф</th>
										</tr>
									</thead>
									<tbody>
										{points.map((point) => {
											const status = STATUS_LABELS[point.status] || { text: String(point.status), color: "inherit" }
											return (
												<tr key={point.pos_id}>
													<td>{point.name} <span style={mutedStyle}>· {point.login}</span></td>
													<td>{point.agent_name || "—"}</td>
													<td style={{ color: status.color }}>{status.text}</td>
													<td>{point.group === "paid" ? "Платил, чеков нет" : "Не платил"}</td>
													<td className="text-center">{point.last_cheque_at ? formatDate(point.last_cheque_at, "dd.MM.yyyy") : "—"}</td>
													<td className="text-end">{point.days_silent}</td>
													<td className="text-center">{point.last_pay_at ? formatDate(point.last_pay_at, "dd.MM.yyyy") : "—"}</td>
													<td className="text-end">{formatNumber(point.paid_in_month)}</td>
													<td className="text-end" style={{ color: point.balance < 0 ? "#d23434" : "inherit" }}>
														{formatNumber(point.balance)}
													</td>
													<td className="text-end">{formatNumber(point.tariff)}</td>
												</tr>
											)
										})}
									</tbody>
								</table>
							</div>
						)}
					</Card>
				</>
			)}
		</>
	)
}

export default Lost
